import { defineStore } from 'pinia';
import i18n, { selectedLocale } from '../i18n.js';
import axios from 'axios';
import f7 from 'framework7';
import { useKeycloakStore } from './keycloak.js';

const path = import.meta.env.VITE_APP_URL;

export const useAuthStore = defineStore('auth', {
  state: () => {
    const lcStorage = window.__piniaLcStorage;
    return {
      keycloak: '',
      token: '',
      refreshToken: '',
      profile: '',
      user: '',
      authenticated: false,
      locale: selectedLocale,
      topath: '',
      loginUrl: '',
      registerUrl: '',
      isNativePlatform: '',
      documentsUrl: [],
      document: {},
      videoService: '',
      videosUrl: [],
      device: '',
      lastSwiperIsActive: false,
      dynamicLink: '',
      adminToken: '',
      adminUser: '',
      csrfToken: '',
      selectedContact: '',
      contactList: [],
      landingPage: [],
      isAuthAvailable: false,
      masterWidth: f7.width,
    };
  },
  persist: true,

  getters: {
    isAuthenticated: (state) => state.token,
    getProfile: (state) => state.profile,
  },

  actions: {
    setToken(token) {
      this.token = token;
    },

    setRefreshToken(refreshToken) {
      this.refreshToken = refreshToken;
    },

    setKeycloak(keycloak) {
      this.keycloak = keycloak;
      if (keycloak.authenticated) {
        this.authenticated = true;
      }
    },

    isAuthenticated_set(authenticated) {
      this.authenticated = authenticated;
    },

    setUser(user) {
      this.user = user;
    },

    setProfile(profile) {
      this.profile = profile;
    },

    changeLocale(newLocale) {
      i18n.locale = newLocale;
      this.locale = newLocale;
    },

    setMasterWidth(payload) {
      console.log(payload);
      this.masterWidth = payload;
    },

    isNativePlatform_set(payload) {
      console.log(payload);
      this.isNativePlatform = payload;
    },

    setDevice(payload) {
      this.device = payload;
    },

    selectDocument(payload) {
      this.document = payload;
    },

    setDocumentsUrl(payload) {
      if (payload === undefined) return;
      const doc = this.documentsUrl.find((item) => item.fileId === payload.fileId);
      const doc1 = this.documentsUrl.find((item) => item.fileName === payload.fileName);
      if (doc) {
        this.documentsUrl.splice(this.documentsUrl.indexOf(doc), 1);
      } else if (doc1) {
        this.documentsUrl.splice(this.documentsUrl.indexOf(doc1), 1);
      }
      this.documentsUrl.push(payload);
    },

    unSetDocumentsUrl(payload) {
      const doc = this.documentsUrl.find(
        (item) =>
          (payload.fileId && item.fileId === payload.fileId) ||
          (payload.fileName && item.fileName === payload.fileName)
      );
      if (doc) {
        this.documentsUrl.splice(this.documentsUrl.indexOf(doc), 1);
      }
    },

    clearDocumentsUrl() {
      this.documentsUrl = [];
    },

    setVideoServiceType(videoService) {
      this.videoService = videoService;
    },

    setVideosUrl(videosUrl) {
      this.videosUrl = videosUrl;
    },

    setLastSwiperIsActive(payload) {
      this.lastSwiperIsActive = payload;
    },

  

    setLandingPage(landingPage) {
      this.landingPage = landingPage;
    },

    setAuthAvailable(isAuthAvailable) {
      this.isAuthAvailable = isAuthAvailable;
    },

    resetProfile() {
      this.profile = '';
      this.user = '';
      this.authenticated = false;
      this.token = '';
      this.refreshToken = '';
      this.keycloak = '';
    },

    setSelectedContact(payload) {
      this.selectedContact = payload;
    },

    setContactList(payload) {
      this.contactList = payload;
    },

    async getCsrfToken() {
      try {
        const { data: csrfToken } = await axios.get(`${path}csrf`);
        this.csrfToken = csrfToken;
      } catch (err) {
        console.log(err);
      }
    },

    async getAdminToken() {
      const params = new URLSearchParams();
      params.append('client_id', 'admin-cli');
      params.append('username', `${import.meta.env.VITE_ADMIN_CREDUSR}`);
      params.append('password', `${import.meta.env.VITE_ADMIN_CREDPWD}`);
      params.append('grant_type', 'password');
      try {
        const { data: adminToken } = await axios.post(
          `${import.meta.env.VITE_ADMIN_TOKEN_URL}`,
          params
        );
        this.adminToken = adminToken;
      } catch (err) {
        console.log(err);
      }
    },

    async getUserFromAdmin() {
      try {
        const { data: adminUser } = await axios.get(
          `${import.meta.env.VITE_ADMIN_USER_URL}/${this.user.id}`,
          {
            headers: {
              Authorization: `Bearer ${this.adminToken.access_token}`,
            },
          }
        );
        this.adminUser = adminUser;
      } catch (err) {
        console.log(err);
      }
    },

    async UpdateUserFromAdmin(payload) {
      try {
        const { data: adminUser } = await axios.put(
          `${import.meta.env.VITE_ADMIN_USER_URL}/${this.user.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${this.adminToken.access_token}`,
            },
          }
        );
        this.adminUser = adminUser;
      } catch (err) {
        console.log(err);
      }
    },

   

    async getPreSignedUrl(payload) {
      const bucket =
        payload.bucketName === null || payload.bucketName === ''
          ? import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME
          : payload.bucketName;
      try {
        const { data: signedUrlList } = await axios({
          url: `${path}/document/signedUrl`,
          method: 'GET',
          data: payload.files,
          params: { bucketName: bucket },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
        for (const signedUrl in signedUrlList) {
          signedUrlList[signedUrl].bucketName = bucket;
        }
        this.signedUrlList = signedUrlList;
      } catch (err) {
        console.log(err);
      }
    },

    async postR2Document(payload) {
      try {
        const bucket = !payload.bucketName
          ? import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME
          : payload.bucketName;
        const isPublic = bucket === import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME;
        // `fileId` uploads a single document, so a group avatar cannot drag the
        // pending message attachments along with it.
        const files = this.documentsUrl
          .filter((doc) => !payload.fileId || doc.fileId === payload.fileId)
          .map((doc) => ({
            fileId: doc.fileId,
            fileName: doc.fileName,
            contentType: doc.contentType,
          }));
        if (files && files.length > 0) {
          const { data: signedUrlList } = await axios({
            url: `${path}document/signedUrl`,
            method: 'POST',
            data: files,
            params: { bucketName: bucket, shortId: payload.shortId },
            headers: {
              Authorization: `Bearer ${useKeycloakStore().token}`,
              'Content-Type': 'application/json',
            },
          });
          for (const [index, signedUrlObj] of signedUrlList.entries()) {
            const currentFile = this.documentsUrl.find(
              (file) =>
                file.fileId === signedUrlObj.fileId && file.file !== null
            );
            const uploadResponse = await fetch(signedUrlObj.signedUrl, {
              method: 'PUT',
              body: currentFile.file,
              headers: { 'Content-Type': signedUrlObj.contentType },
            });
            if (uploadResponse.ok) {
              const documentIndex = this.documentsUrl.findIndex(
                (doc) => doc.fileId === currentFile.fileId
              );
              if (documentIndex !== -1) {
                const { file, ...rest } = this.documentsUrl[documentIndex];
                const updatedDocument = {
                  ...rest,
                  url: isPublic
                    ? `${import.meta.env.VITE_R2_CDN_PUBLIC_ENDPOINT}/${signedUrlObj.fileName}`
                    : `${import.meta.env.VITE_R2_CDN_PRIVATE_ENDPOINT}/${signedUrlObj.fileName}`,
                  fileId: signedUrlObj.shortId,
                };
                this.setDocumentsUrl(updatedDocument);
              }
            } else {
              const documentIndex = this.documentsUrl.findIndex(
                (doc) => doc.fileId === currentFile.fileId
              );
              if (documentIndex !== -1) {
                this.unSetDocumentsUrl({ fileId: currentFile.fileId });
              }
              const errorText = await uploadResponse.text();
              throw new Error(
                `R2 upload failed: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`
              );
            }
          }
        }
      } catch (err) {
        console.log(err);
      }
    },

    async checkEmailAvailability(payload) {
      try {
        const { data: availability } = await axios.get(
          `${path}account/new/register/checkEmailAvailability`,
          { params: { email: payload.email } }
        );
        console.log(availability);
      } catch (err) {
        console.log(err.response?.data);
      }
    },

    async checkPhoneAvailability(payload) {
      try {
        const { data: availability } = await axios.get(
          `${path}account/new/register/checkPhoneAvailability`,
          { params: { phone: payload.phone, phoneCode: payload.phoneCode } }
        );
        console.log(availability);
      } catch (err) {
        console.log(err.response?.data);
      }
    },

    getTopath(payload) {
      this.topath = payload;
    },
  },
});
